import { LanguageCode, Translation } from './types';
import { en } from './i18n/en';
import { pt } from './i18n/pt';
import { es } from './i18n/es';
import { de } from './i18n/de';
import { hi } from './i18n/hi';
import { id } from './i18n/id';
import { it } from './i18n/it';
import { th } from './i18n/th';
import { zhCn } from './i18n/zh-cn';
import { zhTw } from './i18n/zh-tw';
import { vi } from './i18n/vi';
import { tr } from './i18n/tr';
import { fr } from './i18n/fr';
import { ko } from './i18n/ko';
import { ms } from './i18n/ms';
import { ru } from './i18n/ru';
import { ja } from './i18n/ja';
import { ar } from './i18n/ar';
import { pl } from './i18n/pl';

export const LANGUAGES: Record<LanguageCode, { name: string; flag: string }> = {
  en: { name: 'English', flag: '🇺🇸' },
  pt: { name: 'Português', flag: '🇧🇷' },
  es: { name: 'Español', flag: '🇲🇽' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  hi: { name: 'हिन्दी', flag: '🇮🇳' },
  id: { name: 'Bahasa', flag: '🇮🇩' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  th: { name: 'ไทย', flag: '🇹🇭' },
  'zh-cn': { name: '简体中文', flag: '🇨🇳' },
  'zh-tw': { name: '繁體中文', flag: '🇭🇰' },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳' },
  tr: { name: 'Türkçe', flag: '🇹🇷' },
  fr: { name: 'Français', flag: '🇫🇷' },
  ko: { name: '한국어', flag: '🇰🇷' },
  ms: { name: 'Bahasa Melayu', flag: '🇲🇾' },
  ru: { name: 'Русский', flag: '🇷🇺' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ar: { name: 'العربية', flag: '🇸🇦' },
  pl: { name: 'Polski', flag: '🇵🇱' },
};

export const TRANSLATIONS: Record<LanguageCode, Translation> = {
  en,
  pt,
  es,
  de,
  hi,
  id,
  it,
  th,
  'zh-cn': zhCn,
  'zh-tw': zhTw,
  vi,
  tr,
  fr,
  ko,
  ms,
  ru,
  ja,
  ar,
  pl,
};

type SeoKeywordMap = Record<
  LanguageCode,
  {
    default: string[];
    generator: string[];
    scanner: string[];
    blog: string[];
  }
>;

export const SEO_KEYWORDS: SeoKeywordMap = {
  en: {
    default: ['QR Code Generator', 'QR Scanner', 'Free QR Code', 'Barcode Scanner'],
    generator: ['QR Generator', 'Create QR Code', 'Custom QR Code', 'QR Code with Logo'],
    scanner: ['QR Scanner', 'Barcode Scanner', 'Scan QR Online', 'Web QR Reader'],
    blog: ['QR Code Blog', 'QR Marketing Tips', 'QR Technology News', 'QR Code Strategy'],
  },
  pt: {
    default: ['Gerador de QR Code', 'Scanner de QR', 'QRCode Grátis', 'Leitor de Código de Barras'],
    generator: ['Gerar QR Online', 'Criar QR Code', 'QR Code Personalizado', 'QR Code com Logo'],
    scanner: ['Scanner de QR Online', 'Leitor de Código de Barras', 'Escanear QR no Navegador', 'Scanner Web Gratuito'],
    blog: ['Blog QR Code', 'Marketing com QR Code', 'Dicas de QR Code', 'Tendências de QR'],
  },
  es: {
    default: ['Generador de QR', 'Escáner de QR', 'QR Gratis', 'Lector de Códigos'],
    generator: ['Crear Código QR', 'Generador QR Online', 'QR Personalizado', 'QR con Logotipo'],
    scanner: ['Escáner QR Online', 'Lector de Códigos de Barras', 'Escanear QR en la Web', 'Escáner Gratuito'],
    blog: ['Blog de Códigos QR', 'Marketing con QR', 'Noticias de QR', 'Estrategia de Código QR'],
  },
  de: {
    default: ['QR-Code Generator', 'QR Scanner', 'Kostenloser QR-Code', 'Barcode Scanner'],
    generator: ['QR-Code Erstellen', 'QR-Code Generator Online', 'Individueller QR-Code', 'QR mit Logo'],
    scanner: ['QR-Code Scanner Online', 'Barcode Leser', 'QR im Browser scannen', 'Web Scanner'],
    blog: ['QR-Code Blog', 'QR Marketing Tipps', 'QR Trends', 'QR-Strategie'],
  },
  hi: {
    default: ['क्यूआर कोड जनरेटर', 'क्यूआर स्कैनर', 'मुफ़्त क्यूआर कोड', 'बारकोड स्कैनर'],
    generator: ['क्यूआर कोड बनाएं', 'ऑनलाइन क्यूआर जनरेटर', 'कस्टम क्यूआर कोड', 'लोगो वाला क्यूआर'],
    scanner: ['क्यूआर स्कैनर ऑनलाइन', 'बारकोड रीडर', 'वेब क्यूआर स्कैनर', 'मुफ्त क्यूआर स्कैन'],
    blog: ['क्यूआर कोड ब्लॉग', 'क्यूआर मार्केटिंग टिप्स', 'क्यूआर रणनीति', 'डिजिटल क्यूआर गाइड'],
  },
  id: {
    default: ['Generator Kode QR', 'Pemindai QR', 'QR Gratis', 'Pemindai Barcode'],
    generator: ['Buat Kode QR', 'Generator QR Online', 'QR Kustom', 'QR dengan Logo'],
    scanner: ['Pemindai QR Online', 'Pembaca Barcode', 'Pindai QR di Web', 'Pemindai QR Gratis'],
    blog: ['Blog Kode QR', 'Tips Marketing QR', 'Berita Teknologi QR', 'Strategi QR Code'],
  },
  it: {
    default: ['Generatore QR Code', 'Scanner QR', 'QR Gratuito', 'Lettore Barcode'],
    generator: ['Crea QR Code', 'Generatore QR Online', 'QR Personalizzato', 'QR con Logo'],
    scanner: ['Scanner QR Online', 'Lettore Codici a Barre', 'Scanner QR Web', 'Scanner Gratuito'],
    blog: ['Blog QR Code', 'Consigli Marketing QR', 'Notizie QR', 'Strategia QR'],
  },
  th: {
    default: ['ตัวสร้างคิวอาร์โค้ด', 'สแกนเนอร์คิวอาร์', 'คิวอาร์โค้ดฟรี', 'เครื่องอ่านบาร์โค้ด'],
    generator: ['สร้างคิวอาร์โค้ด', 'คิวอาร์โค้ดปรับแต่ง', 'คิวอาร์พร้อมโลโก้', 'เครื่องมือสร้างคิวอาร์ออนไลน์'],
    scanner: ['สแกนคิวอาร์ออนไลน์', 'เครื่องอ่านบาร์โค้ด', 'สแกนคิวอาร์ผ่านเว็บ', 'สแกนเนอร์ฟรี'],
    blog: ['บล็อกคิวอาร์โค้ด', 'เทคนิคการตลาดด้วยคิวอาร์', 'ข่าวเทคโนโลยีคิวอาร์', 'กลยุทธ์คิวอาร์โค้ด'],
  },
  'zh-cn': {
    default: ['二维码生成器', '二维码扫描', '免费二维码', '条形码扫描'],
    generator: ['在线生成二维码', '自定义二维码', 'Logo 二维码', '二维码制作工具'],
    scanner: ['在线扫码', '浏览器扫码', '上传图片扫码', '免费条码扫描器'],
    blog: ['二维码博客', '二维码营销', '二维码趋势', '二维码使用指南'],
  },
  'zh-tw': {
    default: ['QRCode 產生器', 'QR 掃描', '免費 QRCode', '條碼掃描器'],
    generator: ['線上產生 QRCode', '自訂 QRCode', 'Logo QRCode', 'QRCode 製作工具'],
    scanner: ['線上掃碼', '瀏覽器掃碼', '上傳圖片掃碼', '免費條碼掃描器'],
    blog: ['QR 部落格', 'QR 行銷', 'QR 趨勢', 'QRCode 使用指南'],
  },
  vi: {
    default: ['Trình tạo mã QR', 'Trình quét QR', 'QR miễn phí', 'Trình quét mã vạch'],
    generator: ['Tạo QR', 'Tạo mã QR', 'QR tùy chỉnh', 'QR có logo'],
    scanner: ['Trình quét QR', 'Trình quét mã vạch', 'Quét QR online', 'Trình đọc QR web'],
    blog: ['Blog mã QR', 'Mẹo marketing QR', 'Tin tức công nghệ QR', 'Chiến lược QR'],
  },
  tr: {
    default: ['QR Kod Oluşturucu', 'QR Tarayıcı', 'Ücretsiz QR Kod', 'Barkod Tarayıcı'],
    generator: ['QR Oluştur', 'QR Kod Yap', 'Özel QR Kod', 'Logolu QR Kod'],
    scanner: ['Online QR Tarayıcı', 'Barkod Tarayıcı', 'Webde QR Tara', 'Ücretsiz Web QR Okuyucu'],
    blog: ['QR Kod Blog', 'QR Pazarlama İpuçları', 'QR Teknoloji Haberleri', 'QR Kod Stratejisi'],
  },
  fr: {
    default: ['Générateur de QR code', 'Scanner QR', 'QR gratuit', 'Scanner de codes-barres'],
    generator: ['Créer un QR code', 'Générateur QR en ligne', 'QR personnalisé', 'QR avec logo'],
    scanner: ['Scanner QR en ligne', 'Scanner code-barres', 'Scanner QR sur le web', 'Lecteur QR navigateur'],
    blog: ['Blog QR', 'Conseils marketing QR', 'Actualités QR', 'Stratégie QR'],
  },
  ko: {
    default: ['QR 코드 생성기', 'QR 스캐너', '무료 QR 코드', '바코드 스캐너'],
    generator: ['QR 코드 만들기', '온라인 QR 생성', '맞춤 QR 코드', '로고 QR 코드'],
    scanner: ['온라인 QR 스캐너', '바코드 스캐너', '웹에서 QR 스캔', '웹 QR 리더'],
    blog: ['QR 블로그', 'QR 마케팅 팁', 'QR 기술 뉴스', 'QR 전략'],
  },
  ms: {
    default: ['Penjana Kod QR', 'Pengimbas QR', 'Kod QR Percuma', 'Pengimbas Barcode'],
    generator: ['Buat Kod QR', 'Penjana QR Online', 'QR Tersuai', 'QR dengan Logo'],
    scanner: ['Pengimbas QR Online', 'Pengimbas Barcode', 'Imbas QR di Web', 'Pembaca QR Web'],
    blog: ['Blog Kod QR', 'Tip Pemasaran QR', 'Berita Teknologi QR', 'Strategi QR Code'],
  },
  ru: {
    default: ['Генератор QR‑кодов', 'Сканер QR', 'Бесплатный QR‑код', 'Сканер штрих‑кодов'],
    generator: ['Создать QR‑код', 'Онлайн генератор QR', 'Кастомный QR‑код', 'QR с логотипом'],
    scanner: ['Онлайн сканер QR', 'Сканер штрих‑кодов', 'Сканировать QR в браузере', 'Веб‑ридер QR'],
    blog: ['Блог QR‑кодов', 'Советы по QR‑маркетингу', 'Новости QR‑технологий', 'Стратегия QR‑кодов'],
  },
  ja: {
    default: ['QRコード 生成', 'QRスキャナー', '無料 QRコード', 'バーコード スキャナー'],
    generator: ['QRコード 作成', 'オンライン QR生成', 'カスタム QRコード', 'ロゴ QRコード'],
    scanner: ['QRスキャナー', 'バーコード スキャナー', 'オンライン QRスキャン', 'Web QRリーダー'],
    blog: ['QRブログ', 'QRマーケティング', 'QR技術ニュース', 'QR戦略'],
  },
  ar: {
    default: ['منشئ QR', 'ماسح QR', 'QR مجاني', 'ماسح باركود'],
    generator: ['إنشاء QR', 'منشئ QR أونلاين', 'QR مخصص', 'QR مع شعار'],
    scanner: ['ماسح QR أونلاين', 'ماسح باركود', 'مسح QR على الويب', 'قارئ QR في المتصفح'],
    blog: ['مدونة QR', 'نصائح تسويق QR', 'أخبار تقنية QR', 'استراتيجية QR'],
  },
  pl: {
    default: ['Generator QR', 'Skaner QR', 'Darmowy QR', 'Skaner kodów kreskowych'],
    generator: ['Tworzenie QR', 'Generator QR online', 'Własny kod QR', 'QR z logo'],
    scanner: ['Skaner QR online', 'Skaner kodów kreskowych', 'Skanuj QR w przeglądarce', 'Czytnik QR web'],
    blog: ['Blog QR', 'Marketing QR', 'Aktualności QR', 'Strategia QR'],
  },
};
